import type { ReactNode } from "react";
import { Card, LabelChip, Text } from "@carbon6/c6-frontend-design";
import type { MemoPreview } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { MemoCardPreview } from "@/components/memo";
import { HatTooltip } from "@/components/tooltip";
import { DatasetPreview } from "./DatasetPreview";
import { datasetStorageBadge } from "./DatasetStorageBadge";
import styles from "./DatasetCard.module.css";
import type {
  DatasetResourceBadge,
  DatasetResourceCardModel,
  DatasetResourceChip,
  DatasetResourceStatus,
} from "./datasetResourceModels";

export type DatasetCardItem = DatasetResourceCardModel;

export type DatasetCardProps = {
  item: DatasetCardItem;
  actions?: ReactNode;
  // 카드 위쪽에 두는 경고 버튼들이다. 예) 미처리 품질 후보, 채널 누락. 없으면 null 이다.
  warningActions?: ReactNode;
  imageUrl?: string | null;
  onImageError?: () => void;
  // 메모(댓글) 표면. uuid 가 있으면 Card.Body 하단에 최근 메모 미리보기를 그린다.
  // recentMemo와 memoCount가 없으면 메모 작성 진입점만 표시한다.
  memoDatasetUuid?: string;
  recentMemo?: MemoPreview | null;
  memoCount?: number;
};

const visibleChipCount = 3;

export function DatasetCard({
  item,
  actions = null,
  warningActions = null,
  imageUrl = null,
  onImageError,
  memoDatasetUuid,
  recentMemo,
  memoCount,
}: DatasetCardProps): ReactNode {
  const cardBadges = renderCardBadges(item.badges);

  return (
    <Card
      layout="horizontal"
      variant="outlined"
      className={styles.card}
      aria-label={item.title}
      data-kind={item.kind}
    >
      <Card.Media percentage={33} aspectRatio="1/1" className={styles.media}>
        <DatasetPreview
          label={item.title}
          imageUrl={imageUrl}
          mode="card"
          onImageError={onImageError}
        />
      </Card.Media>
      <Card.Header
        title={
          // 저장 위치 칩은 이름 옆에 둔다. 표의 이름 칸, 상세 제목과 같은 자리다. 액션 아래에 두면 칩이
          // 있는 카드만 본문이 한 줄 내려간다.
          <span className={styles.titleLine}>
            <span className={styles.title}>{item.title}</span>
            {item.storageBackend === null ? null : datasetStorageBadge(item.storageBackend)}
          </span>
        }
        actions={
          <div className={styles.statusGroup}>
            {actions ? <div className={styles.actions}>{actions}</div> : null}
            {renderStatus(item.status)}
            {cardBadges}
          </div>
        }
      />
      <Card.Body>
        {warningActions === null ? null : (
          <div className={styles.warningActions}>{warningActions}</div>
        )}
        <dl className={styles.metaList}>
          {item.metaItems.map((metaItem) => (
            <div key={metaItem.label} title={metaItem.title} data-span={metaItem.span}>
              <dt>{metaItem.label}</dt>
              <dd>{metaItem.value}</dd>
            </div>
          ))}
        </dl>

        {renderResourceChips(item.chips, item.derivedChips)}

        {memoDatasetUuid ? (
          <MemoCardPreview datasetUuid={memoDatasetUuid} latest={recentMemo} count={memoCount} />
        ) : null}
      </Card.Body>
    </Card>
  );
}

function renderStatus(status: DatasetResourceStatus | null): ReactNode {
  if (status === null) {
    return null;
  }

  return (
    <Text
      variant={hatcheryTextModes["caption-bold"]}
      className={styles.status}
      data-tone={status.tone}
      title={status.title ?? status.label}
    >
      {status.label}
    </Text>
  );
}

function renderCardBadges(badges: readonly DatasetResourceBadge[]): ReactNode {
  if (badges.length === 0) {
    return null;
  }

  return (
    <div className={styles.badges}>
      {badges.map((badge) => (
        <Text
          key={badge.label}
          variant={hatcheryTextModes["caption-bold"]}
          className={styles.badge}
          title={badge.title ?? badge.label}
        >
          {badge.label}
        </Text>
      ))}
    </div>
  );
}

// 칩 줄 = 파생 칩(전부) + 라벨 칩(앞에서 3개) + `+N`. 파생 칩은 라벨 개수 제한에 들지 않는다.
// 그래서 `+N` 과 오버플로 툴팁은 **라벨 칩만** 센다. 항상 보이는 칩을 숨은 칩으로 세지 않는다.
function renderResourceChips(
  chips: readonly DatasetResourceChip[],
  derivedChips: readonly DatasetResourceChip[],
): ReactNode {
  if (chips.length === 0 && derivedChips.length === 0) {
    return (
      <Text variant={hatcheryTextModes["caption-normal"]} className={styles.emptyLabels}>
        등록된 라벨이 없습니다.
      </Text>
    );
  }

  const visibleChips = chips.slice(0, visibleChipCount);
  const hiddenCount = chips.length - visibleChips.length;

  return (
    <HatTooltip
      content={hiddenCount > 0 ? renderResourceChipTooltip(chips) : null}
      disabled={hiddenCount <= 0}
      placement="bottom"
      mode="plain"
    >
      <div className={styles.labels}>
        {derivedChips.map((chip, index) => renderChip(chip, `derived-${index}`, false))}
        {visibleChips.map((chip, index) => renderChip(chip, `label-${index}`, true))}
        {hiddenCount > 0 ? (
          <Text variant={hatcheryTextModes["caption-bold"]} className={styles.hiddenCount}>
            +{hiddenCount}
          </Text>
        ) : null}
      </div>
    </HatTooltip>
  );
}

// `showIcon` 이 라벨 칩과 파생 칩을 구분한다. 색 swatch 는 라벨의 서버 색을 보여 준다.
// 파생 축(수집 방식)에는 색이 없다. 그래서 swatch 를 빼서 다른 종류의 칩임을 보여 준다.
function renderChip(chip: DatasetResourceChip, key: string, showIcon: boolean): ReactNode {
  return (
    <LabelChip
      key={key}
      className={styles.label}
      color={chip.color}
      size="xs"
      variant="filled"
      showIcon={showIcon}
      title={chip.title ?? chip.label}
    >
      {chip.label}
    </LabelChip>
  );
}

function renderResourceChipTooltip(chips: readonly DatasetResourceChip[]): ReactNode {
  return (
    <span className={styles.labelTooltipList}>
      {chips.map((chip, index) => (
        <LabelChip
          key={`${chip.label}-${index}`}
          className={styles.labelTooltipItem}
          color={chip.color}
          size="xs"
          variant="filled"
          title={chip.title ?? chip.label}
        >
          {chip.label}
        </LabelChip>
      ))}
    </span>
  );
}
